/**
 * ToolConfigurationTab — the edit form of an existing tool (entity-management §3: a standard inline form on the detail
 * page). Demo page: /form-layouts. Field help is the (?) popover of each field.
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createOrUpdateToolMutation,
  getToolQueryKey,
  listToolsQueryKey,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Card, useToast } from '../../../components/ui/index.js';
import { Form, FormActions, useZodForm } from '../../../components/form/index.js';
import { Can } from '../../../components/auth/Can.js';
import type { Tool } from '../../../generated/api/types.gen.js';
import { ToolFields, toolFormSchema, toolFormToUpsert, toolToFormValues, type ToolFormValues } from '../ToolForm.js';

export const ToolConfigurationTab: React.FC<{ tool: Tool }> = ({ tool }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const save = useMutation(createOrUpdateToolMutation());
  const defaults = toolToFormValues(tool);
  const form = useZodForm(toolFormSchema, { defaultValues: defaults });
  // The JSON editors keep their own text; bump the key when the form is reset so they re-read the values.
  const [revision, setRevision] = React.useState(0);

  const submit = async (values: ToolFormValues) => {
    await save.mutateAsync({ path: { name: tool.name }, body: toolFormToUpsert(values) });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getToolQueryKey({ path: { name: tool.name } }) }),
      queryClient.invalidateQueries({ queryKey: listToolsQueryKey() }),
    ]);
    form.reset(values);
    notify('Tool saved');
  };

  const reset = () => {
    form.reset(defaults);
    setRevision((current) => current + 1);
  };

  return (
    <Can
      policy="CanManageSkills"
      fallback={
        <Card title="Configuration">
          <p className="text-sm text-muted">You do not have permission to change tools.</p>
        </Card>
      }
    >
      <Card title="Configuration">
        <Form form={form} onSubmit={submit} guardUnsaved className="space-y-8">
          <ToolFields nameEditable={false} currentCategory={defaults.category} revision={revision} />
          <FormActions submitLabel="Save changes" onCancel={reset} cancelLabel="Reset" />
        </Form>
      </Card>
    </Can>
  );
};
